import { io, Socket } from "socket.io-client";
import { toast } from "react-toastify";
import { store } from "../store";
import { setOrderNotification } from "../slices/ecommerce/reducer";
import { viewOrder } from "../pages/Orders/_requests";
import { userSubscriptionViewDetailsApi } from "../pages/Subscription/_requests";

// Extract base URL from REACT_APP_API_URL (remove /v1)
const apiUrl = process.env.REACT_APP_API_URL || "http://localhost:3000/v1";
const socketBaseUrl = apiUrl.replace("/v1", "");

let socket: Socket;

const triggerDesktopNotification = (title: string, body: string, type: string, id: any) => {
    if (!("Notification" in window)) return;

    if (Notification.permission === "granted") {
        const notification = new Notification(title, {
            body: body,
            icon: "/favicon.ico",
        });

        notification.onclick = () => {
            window.focus();
            if (type === "ORDER") {
                window.location.href = `/orders/view/${id}`;
            } else if (type === "SUBSCRIPTION") {
                window.location.href = `/view-user-subscription/${id}`;
            }
        };
    } else if (Notification.permission !== "denied") {
        Notification.requestPermission().then(permission => {
            if (permission === "granted") {
                triggerDesktopNotification(title, body, type, id);
            }
        });
    }
};

export const initSocket = () => {
    socket = io(socketBaseUrl, {
        transports: ["websocket", "polling"],
        reconnection: true,
        reconnectionDelay: 5000,
    });

    socket.on("connect", () => {
        console.log("Connected to Backend Socket Server:", socket.id);
        socket.emit("join-admin-room");
    });

    socket.on("order_created", async (data: any) => {
        console.log("New Order Received (Socket):", data);
        
        // Play notification sound
        const audio = new Audio("/notification/notification.mp3");
        audio.play().catch(e => console.log("Sound play error", e));

        let fullOrderData = null;
        try {
            // Fetch full details from API to ensure everything (phone, products, etc.) is available
            const res = await viewOrder(data.id || data.order_id);
            fullOrderData = res.data;

            // Dispatch action to show modal with full data
            store.dispatch(setOrderNotification({
                type: "NEW_ORDER",
                title: "New Order Placed!",
                order_id: data.id || data.order_id,
                order_code_id: fullOrderData?.order_code_id || data.order_code_id,
                data: fullOrderData
            }));
        } catch (error) {
            console.error("Failed to fetch full order details:", error);
            // Fallback to socket data if API fails
            store.dispatch(setOrderNotification({
                type: "NEW_ORDER",
                title: "New Order Placed!",
                order_id: data.id || data.order_id,
                order_code_id: data.order_code_id,
                data: data
            }));
        }

        toast.info(`New Order Placed: ${data.order_code_id}`, {
            position: "top-right",
            autoClose: 5000,
        });

        // Trigger Desktop Notification
        triggerDesktopNotification(
            "New Order Placed!",
            `Order: ${data.order_code_id} from ${fullOrderData?.user?.name || 'Customer'}`,
            "ORDER",
            data.id || data.order_id
        );

        // Dispatch global event for NotificationDropdown to refresh
        window.dispatchEvent(new CustomEvent("new-socket-notification", { detail: { category: "ORDER" } }));
    });

    socket.on("order_status_updated", async (data: any) => {
        console.log("Order Status Updated (Socket):", data);
        
        // Play notification sound
        const audio = new Audio("/notification/notification.mp3");
        audio.play().catch(e => console.log("Sound play error", e));

        let fullOrderData = null;
        try {
            // Fetch full details from API
            const res = await viewOrder(data.order_id || data.id);
            fullOrderData = res.data;

            // Dispatch action to show modal with full data
            store.dispatch(setOrderNotification({
                type: "STATUS_UPDATE",
                title: "Order Status Updated!",
                order_id: data.order_id || data.id,
                data: fullOrderData
            }));
        } catch (error) {
            console.error("Failed to fetch full order details:", error);
            store.dispatch(setOrderNotification({
                type: "STATUS_UPDATE",
                title: "Order Status Updated!",
                order_id: data.order_id || data.id,
                data: data
            }));
        }

        toast.info(`Order Status Updated for ${data.order_id}`, {
            position: "top-right",
            autoClose: 3000,
        });

        // Trigger Desktop Notification
        triggerDesktopNotification(
            "Order Status Updated!",
            `Order ${fullOrderData?.order_code_id || data.order_id} is now ${fullOrderData?.order_status?.name || 'Updated'}`,
            "ORDER",
            data.order_id || data.id
        );

        // Dispatch global event for NotificationDropdown
        window.dispatchEvent(new CustomEvent("new-socket-notification", { detail: { category: "ORDER" } }));
    });

    socket.on("subscription_created", async (data: any) => {
        console.log("New Subscription Received (Socket):", data);
        
        // Play notification sound
        const audio = new Audio("/notification/notification.mp3");
        audio.play().catch(e => console.log("Sound play error", e));

        let fullSubData = null;
        try {
            // Fetch full details
            const res = await userSubscriptionViewDetailsApi(data.subscription_id || data.id);
            fullSubData = res.data;

            store.dispatch(setOrderNotification({
                type: "NEW_SUBSCRIPTION",
                title: "New Subscription Created!",
                order_id: data.subscription_id || data.id,
                order_code_id: fullSubData?.user_subscription_order_id || data.order_code,
                data: fullSubData
            }));
        } catch (error) {
            console.error("Failed to fetch full subscription details:", error);
            store.dispatch(setOrderNotification({
                type: "NEW_SUBSCRIPTION",
                title: "New Subscription Created!",
                order_id: data.subscription_id || data.id,
                order_code_id: data.order_code,
                data: data
            }));
        }

        toast.info(`New Subscription: ${data.order_code}`, {
            position: "top-right",
            autoClose: 5000,
        });

        // Trigger Desktop Notification
        triggerDesktopNotification(
            "New Subscription Created!",
            `Plan: ${fullSubData?.type_name || ''} - Sub ID: ${data.order_code}`,
            "SUBSCRIPTION",
            data.subscription_id || data.id
        );

        // Dispatch global event for NotificationDropdown
        window.dispatchEvent(new CustomEvent("new-socket-notification", { detail: { category: "SUBSCRIPTION" } }));
    });

    socket.on("disconnect", () => {
        console.log("Disconnected from Socket Server");
    });

    return socket;
};

export const getSocket = () => socket;
